// Parts and stock (UX §6.3 silhouettes):
//   Slug, Casing: single → paper sleeve of 2 → tray of 4 → stencilled box
//   Powder: scoop → pouch → jar → keg
//   Copper / Steel bar (cbar, sbar): nugget → bar → ingot → block (stack)
//   Barrel, Frame (v0.2): stubby → long → heavy tubes; grip frame → heavy frame
// Packaging bands carry the tier colour (UX §6.1 "tier ramp: badge fill, packaging bands, glow").

import type { Swatch } from '../palette.ts';
import type { Part } from '../passes.ts';
import { box, type Pt, rotated, rrect } from '../shapes.ts';
import type { ItemKit } from './kit.ts';

type Unit = 'slug' | 'casing';

/** A face drawn flat in a derived tone (top faces of bars and boxes). */
function flatSw(sw: Swatch, key: 'light' | 'hi' | 'shade'): Swatch {
  return { ...sw, base: sw[key] };
}

function unitParts(k: ItemKit, u: Unit, cx: number, top: number, w: number, h: number): Part[] {
  if (u === 'slug') {
    const lead = k.sw('material.lead');
    return [
      k.rect('material.lead', cx - w / 2, top, w, h, [w / 2, w / 2, w * 0.14, w * 0.14], {
        detail: () =>
          k.stroke(
            [
              [cx - w * 0.42, top + h * 0.72],
              [cx + w * 0.42, top + h * 0.72],
            ],
            lead.outline,
            k.ow * 0.5,
          ),
      }),
    ];
  }
  const cas = k.sw('material.casing');
  return [
    k.rect('material.casing', cx - w / 2, top, w, h, w * 0.14, {
      detail: () => {
        k.fillEll(cx, top + w * 0.2, w * 0.36, w * 0.14, cas.outline);
        k.stroke(
          [
            [cx - w * 0.42, top + h * 0.8],
            [cx + w * 0.42, top + h * 0.8],
          ],
          cas.outline,
          k.ow * 0.5,
        );
      },
    }),
  ];
}

function tierBand(k: ItemKit, fx: number, fy: number, fw: number, fh: number): void {
  k.fillRect(fx, fy, fw, fh, 0, k.pal.hex(k.pal.tier(k.tier)));
}

export function unitLadder(k: ItemKit, u: Unit): Part[] {
  switch (Math.min(4, k.tier)) {
    case 1:
      return u === 'slug'
        ? unitParts(k, u, 0.5, 0.06, 0.5, 0.9)
        : unitParts(k, u, 0.5, 0.05, 0.48, 0.92);
    case 2: {
      const sleeve = k.rect('ui.cream2', 0.06, 0.44, 0.88, 0.52, 0.08, {
        detail: () => tierBand(k, 0.06, 0.6, 0.88, 0.12),
      });
      return [
        ...unitParts(k, u, 0.31, 0.04, 0.38, 0.8),
        ...unitParts(k, u, 0.69, 0.04, 0.38, 0.8),
        sleeve,
      ];
    }
    case 3: {
      const units = [0.14, 0.38, 0.62, 0.86].flatMap((cx) => unitParts(k, u, cx, 0.1, 0.22, 0.62));
      const lip = k.rect('mach.front', 0, 0.5, 1, 0.46, 0.08, {
        detail: () => tierBand(k, 0, 0.64, 1, 0.1),
      });
      return [...units, lip];
    }
    default: {
      const tok = u === 'slug' ? 'plate.steel' : 'machine.crate';
      const sw = k.sw(tok);
      const top = k.poly(
        tok,
        [
          [0.14, 0.06],
          [0.86, 0.06],
          [1, 0.3],
          [0, 0.3],
        ],
        0.03,
        {
          sw: flatSw(sw, 'light'),
          flat: true,
          detail: () => tierBand(k, 0.42, 0.06, 0.16, 0.24),
        },
      );
      const front = k.rect(tok, 0, 0.3, 1, 0.66, 0.06, {
        detail: () => {
          // stencil: the unit's silhouette in cream
          const st = k.pal.hex('ui.cream2');
          if (u === 'slug') k.fillRect(0.4, 0.42, 0.2, 0.42, 0.1, st);
          else {
            k.fillRect(0.4, 0.44, 0.2, 0.4, 0.03, st);
            k.fillEll(0.5, 0.48, 0.07, 0.03, sw.base);
          }
          tierBand(k, 0, 0.62, 0.3, 0.1);
          tierBand(k, 0.7, 0.62, 0.3, 0.1);
        },
      });
      return [top, front];
    }
  }
}

export function powderLadder(k: ItemKit): Part[] {
  const powder = k.sw('material.powder');
  const grains = (cx: number, cy: number, rx: number, ry: number, n: number) => {
    const glint = k.pal.sw('material.coalGlint').base;
    for (let i = 0; i < n; i++) {
      const a = k.rng() * Math.PI * 2;
      const d = Math.sqrt(k.rng());
      k.fillEll(cx + Math.cos(a) * rx * d, cy + Math.sin(a) * ry * d, 0.018, 0.018, glint);
    }
  };
  switch (Math.min(4, k.tier)) {
    case 1: {
      const handle = k.poly(
        'material.steel',
        [
          [0.74, 0.5],
          [0.9, 0.12],
          [1, 0.16],
          [0.86, 0.56],
        ],
        0.04,
      );
      const mound = k.ell('material.powder', 0.43, 0.5, 0.36, 0.24, {
        detail: () => grains(0.43, 0.46, 0.26, 0.12, 7),
      });
      const bowl = k.poly(
        'material.steel',
        [
          [0.02, 0.52],
          [0.84, 0.52],
          [0.74, 0.84],
          [0.5, 0.94],
          [0.26, 0.94],
          [0.12, 0.84],
        ],
        0.1,
      );
      return [handle, mound, bowl];
    }
    case 2: {
      const top = k.ell('material.powder', 0.5, 0.22, 0.2, 0.08);
      const pouch = k.smooth(
        'ui.cream2',
        [
          [k.X(0.28), k.Y(0.18)],
          [k.X(0.72), k.Y(0.18)],
          [k.X(0.64), k.Y(0.34)],
          [k.X(0.98), k.Y(0.66)],
          [k.X(0.86), k.Y(1)],
          [k.X(0.14), k.Y(1)],
          [k.X(0.02), k.Y(0.66)],
          [k.X(0.36), k.Y(0.34)],
        ] satisfies Pt[],
        {
          detail: () => {
            k.stroke(
              [
                [0.33, 0.33],
                [0.5, 0.36],
                [0.67, 0.33],
              ],
              k.pal.sw('ui.cream2').outline,
              k.ow * 0.6,
            );
            const lab = k.pal.hex('material.powderLabel');
            k.c.beginPath();
            k.c.moveTo(k.X(0.5), k.Y(0.5));
            k.c.lineTo(k.X(0.66), k.Y(0.66));
            k.c.lineTo(k.X(0.5), k.Y(0.82));
            k.c.lineTo(k.X(0.34), k.Y(0.66));
            k.c.closePath();
            k.c.fillStyle = lab;
            k.c.fill();
            tierBand(k, 0.44, 0.62, 0.12, 0.08);
          },
        },
      );
      return [top, pouch];
    }
    case 3: {
      const jar = k.rect('glow.select', 0.12, 0.2, 0.76, 0.78, 0.16, {
        detail: () => {
          k.fillRect(0.2, 0.38, 0.6, 0.54, 0.1, powder.base);
          grains(0.5, 0.64, 0.24, 0.2, 9);
          k.fillRect(0.12, 0.56, 0.76, 0.2, 0, k.pal.hex('material.powderLabel'));
          tierBand(k, 0.12, 0.62, 0.76, 0.07);
        },
      });
      const lid = k.rect('material.steel', 0.06, 0.04, 0.88, 0.2, 0.06);
      return [jar, lid];
    }
    default: {
      const wood = k.sw('frame.wood');
      const keg = k.poly(
        'frame.wood',
        [
          [0.2, 0.08],
          [0.8, 0.08],
          [0.98, 0.52],
          [0.8, 0.98],
          [0.2, 0.98],
          [0.02, 0.52],
        ],
        0.14,
        {
          detail: () => {
            for (const fy of [0.26, 0.78]) {
              k.stroke(
                [
                  [0.1, fy],
                  [0.9, fy],
                ],
                k.pal.hex('mach.front'),
                k.ow * 1.2,
              );
            }
            for (const fx of [0.36, 0.64]) {
              k.stroke(
                [
                  [fx, 0.12],
                  [fx, 0.94],
                ],
                wood.shade,
                k.ow * 0.4,
              );
            }
            k.fillEll(0.5, 0.52, 0.2, 0.17, k.pal.hex('material.powderLabel'));
            tierBand(k, 0.38, 0.49, 0.24, 0.07);
          },
        },
      );
      const lid = k.ell('ui.wood', 0.5, 0.1, 0.3, 0.07, { flat: true });
      return [keg, lid];
    }
  }
}

function bar(
  k: ItemKit,
  tok: string,
  x: number,
  y: number,
  w: number,
  h: number,
  stamp: boolean,
): Part[] {
  const sw = k.sw(tok);
  const inset = w * 0.12;
  const top = k.poly(
    tok,
    [
      [x + inset, y],
      [x + w - inset, y],
      [x + w - inset * 0.3, y + h * 0.42],
      [x + inset * 0.3, y + h * 0.42],
    ],
    0.02,
    {
      sw: flatSw(sw, 'light'),
      flat: true,
      ...(stamp
        ? { detail: () => k.fillEll(x + w / 2, y + h * 0.21, w * 0.12, h * 0.1, sw.shade) }
        : {}),
    },
  );
  const front = k.poly(
    tok,
    [
      [x + inset * 0.3, y + h * 0.42],
      [x + w - inset * 0.3, y + h * 0.42],
      [x + w, y + h],
      [x, y + h],
    ],
    0.02,
  );
  return [top, front];
}

export function barLadder(k: ItemKit, tok: string): Part[] {
  switch (Math.min(4, k.tier)) {
    case 1: {
      const sw = k.sw(tok);
      const pts: Pt[] = [];
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const j = 1 + (k.rng() - 0.5) * 0.12;
        pts.push([k.X(0.5 + Math.cos(a) * 0.5 * j), k.Y(0.56 + Math.sin(a) * 0.38 * j)]);
      }
      return [
        k.smooth(tok, pts, {
          detail: () => k.fillEll(0.36, 0.4, 0.1, 0.06, sw.hi),
        }),
      ];
    }
    case 2:
      return bar(k, tok, 0.02, 0.2, 0.96, 0.6, false);
    case 3:
      return bar(k, tok, 0, 0.08, 1, 0.84, true);
    default:
      return [
        ...bar(k, tok, 0.24, 0.02, 0.52, 0.46, false),
        ...bar(k, tok, 0, 0.48, 0.5, 0.5, false),
        ...bar(k, tok, 0.5, 0.48, 0.5, 0.5, false),
      ];
  }
}

export function barrelLadder(k: ItemKit): Part[] {
  const t = Math.min(4, k.tier);
  // T4 stays inside the cell with its halo at full scale (rotated extent ≈ 0.5 of the item box)
  const len = [0.88, 1.0, 1.18, 0.96][t - 1] ?? 0.8;
  const th = [0.42, 0.3, 0.22, 0.38][t - 1] ?? 0.3;
  const cx = k.X(0.5);
  const cy = k.Y(0.5);
  const L = k.S(len);
  const T = k.S(th);
  const sw = k.sw('material.steel');
  const path = rotated(rrect(cx - L / 2, cy - T / 2, L, T, T * 0.2), cx, cy, -32);
  const span = (L * Math.cos((32 * Math.PI) / 180) + T * Math.sin((32 * Math.PI) / 180)) / 2;
  const spanY = (L * Math.sin((32 * Math.PI) / 180) + T * Math.cos((32 * Math.PI) / 180)) / 2;
  return [
    {
      path,
      sw,
      box: box(cx - span, cy - spanY, span * 2, spanY * 2),
      detail: (c) => {
        c.save();
        c.translate(cx, cy);
        c.rotate((-32 * Math.PI) / 180);
        c.beginPath();
        c.ellipse(L / 2 - T * 0.12, 0, T * 0.14, T * 0.34, 0, 0, Math.PI * 2);
        c.fillStyle = sw.outline;
        c.fill();
        const rings = t === 4 ? [-0.3, -0.1, 0.1] : t === 3 ? [-0.36] : [];
        for (const r of rings) {
          c.fillStyle = sw.shade;
          c.fillRect(L * r, -T / 2, T * 0.18, T);
        }
        c.fillStyle = k.pal.hex(k.pal.tier(k.tier));
        c.fillRect(-L / 2 + T * 0.15, -T / 2, T * 0.22, T);
        c.restore();
      },
    },
  ];
}

export function frameLadder(k: ItemKit): Part[] {
  const t = Math.min(4, k.tier);
  const heavy = t >= 3 ? 0.06 : 0;
  const sw = k.sw('material.iron');
  const body = k.poly(
    'material.iron',
    [
      [0.04, 0.12 - heavy],
      [0.96, 0.12 - heavy],
      [0.96, 0.42 + heavy],
      [0.56, 0.42 + heavy],
      [0.5, 0.96],
      [0.14, 0.96],
      [0.24, 0.42 + heavy],
      [0.04, 0.42 + heavy],
    ],
    0.05,
    {
      detail: () => {
        for (let i = 0; i < 3; i++) {
          const fy = 0.56 + i * 0.12;
          k.stroke(
            [
              [0.24 - i * 0.02, fy],
              [0.46 - i * 0.02, fy],
            ],
            sw.shade,
            k.ow * 0.5,
          );
        }
        if (t >= 3)
          for (const fx of [0.14, 0.86])
            k.fillEll(fx, 0.27, 0.035, 0.035, k.pal.hex('material.steel'));
        k.fillRect(0.66, 0.2, 0.2, 0.08, 0.02, k.pal.hex(k.pal.tier(k.tier)));
      },
    },
  );
  const parts: Part[] = [body];
  if (t >= 2) {
    const guard = k.poly(
      'material.iron',
      [
        [0.52, 0.42],
        [0.74, 0.42],
        [0.74, 0.62],
        [0.52, 0.66],
      ],
      0.06,
      { detail: () => k.fillEll(0.64, 0.52, 0.06, 0.05, sw.outline) },
    );
    parts.unshift(guard);
  }
  if (t >= 4) parts.push(k.rect('material.steel', 0.1, 0.14, 0.44, 0.24, 0.05));
  return parts;
}
